import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Pencil, Trash2, Sparkles, Save, X as XIcon, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Personal "My Words" editor. Manual edits only — no PDF / photo upload.
export default function MyWordsEdit() {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newWord, setNewWord] = useState("");
  // editing: { original: "apple", draft: "apples" } | null
  const [editing, setEditing] = useState(null);
  const [busyWord, setBusyWord] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wordlists/my-words");
      setList(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load My Words");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sanitize = (s) => String(s || "").trim().toLowerCase().replace(/[^a-z]/g, "");

  const onAdd = async (e) => {
    e?.preventDefault?.();
    const w = sanitize(newWord);
    if (w.length < 2) {
      toast.error("Use at least 2 letters (a–z only)");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post(`/wordlists/${list.id}/words`, { word: w });
      setList(data);
      setNewWord("");
      toast.success(`Added "${w}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add word");
    } finally {
      setAdding(false);
    }
  };

  const onSaveEdit = async () => {
    if (!editing) return;
    const next = sanitize(editing.draft);
    if (next.length < 2) {
      toast.error("Use at least 2 letters (a–z only)");
      return;
    }
    if (next === editing.original) { setEditing(null); return; }
    setBusyWord(editing.original);
    try {
      const { data } = await api.patch(
        `/wordlists/${list.id}/words/${encodeURIComponent(editing.original)}`,
        { word: next }
      );
      setList(data);
      setEditing(null);
      toast.success("Word updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setBusyWord(null);
    }
  };

  const onRemove = async (word) => {
    if (!window.confirm(`Remove "${word}" from My Words?`)) return;
    setBusyWord(word);
    try {
      const { data } = await api.delete(`/wordlists/${list.id}/words/${encodeURIComponent(word)}`);
      setList(data);
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    } finally {
      setBusyWord(null);
    }
  };

  if (loading || !list) {
    return (
      <div className="card-soft text-slate-500 flex items-center gap-2 font-semibold">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="my-words-edit-page">
      <Link to="/wordlists" className="inline-flex items-center gap-1 text-sm font-bold text-[#4D96FF] hover:underline" data-testid="my-words-back">
        <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back to word lists
      </Link>

      <header className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-2xl bg-[#FBBF24] flex items-center justify-center shadow-[0_6px_0_#B45309] shrink-0">
          <Sparkles className="w-7 h-7 text-[#1E1B4B]" strokeWidth={3} />
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">My Words</h1>
          <p className="text-slate-600 font-semibold text-sm">
            Your private list. Type a word to add it — no photos or PDFs needed.
          </p>
        </div>
      </header>

      <form onSubmit={onAdd} className="card-soft" data-testid="my-words-add-form">
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Add a word</label>
        <div className="flex gap-2">
          <input
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            placeholder="Type a spelling word"
            className="flex-1 rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-4 py-3 outline-none font-semibold"
            maxLength={20}
            data-testid="my-words-add-input"
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={adding || !newWord.trim()}
            className="btn-clay-blue disabled:opacity-50"
            data-testid="my-words-add-btn"
          >
            {adding ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" strokeWidth={3} />}
            Add
          </button>
        </div>
        <p className="text-xs text-slate-500 font-semibold mt-2">
          Letters only. We'll lowercase your word automatically.
        </p>
      </form>

      <div className="card-soft" data-testid="my-words-list-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold text-[#1E1B4B]">
            Your words <span className="text-slate-500 text-base">({list.word_count})</span>
          </h2>
          {list.word_count > 0 && (
            <Link to={`/wordlists/${list.id}`} className="text-sm font-bold text-[#4D96FF] hover:underline inline-flex items-center gap-1">
              <BookOpen className="w-4 h-4" strokeWidth={3} /> View as cards
            </Link>
          )}
        </div>
        {list.word_count === 0 ? (
          <div className="text-center py-8 text-slate-500 font-semibold" data-testid="my-words-empty">
            No words yet. Add one above to get started.
          </div>
        ) : (
          <ul className="divide-y divide-blue-100" data-testid="my-words-list">
            {list.words.map((w, i) => {
              const isEditing = editing?.original === w;
              return (
                <motion.li
                  key={w}
                  initial={{ x: -6, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                  className="flex items-center justify-between py-2.5 gap-2"
                  data-testid={`my-words-row-${w}`}
                >
                  {isEditing ? (
                    <>
                      <input
                        value={editing.draft}
                        onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") onSaveEdit();
                          if (e.key === "Escape") setEditing(null);
                        }}
                        autoFocus
                        maxLength={20}
                        className="flex-1 rounded-xl border-4 border-amber-200 focus:border-[#FBBF24] px-3 py-1.5 outline-none font-semibold"
                        data-testid={`my-words-edit-input-${w}`}
                      />
                      <button
                        onClick={onSaveEdit}
                        disabled={busyWord === w}
                        className="px-3 py-1.5 rounded-2xl bg-[#22C55E] text-white text-sm font-bold shadow-[0_3px_0_#15803D] inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`my-words-save-${w}`}
                      >
                        <Save className="w-4 h-4" strokeWidth={3} /> Save
                      </button>
                      <button
                        onClick={() => setEditing(null)}
                        className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center"
                        title="Cancel"
                        data-testid={`my-words-cancel-${w}`}
                      >
                        <XIcon className="w-4 h-4" strokeWidth={3} />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-7 h-7 rounded-full bg-blue-50 text-[#4D96FF] flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </span>
                        <span className="font-display text-lg font-bold text-[#1E1B4B] truncate">{w}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => setEditing({ original: w, draft: w })}
                          disabled={!!busyWord}
                          className="w-9 h-9 rounded-full bg-blue-50 hover:bg-blue-100 text-[#4D96FF] flex items-center justify-center disabled:opacity-50"
                          title="Edit"
                          data-testid={`my-words-edit-btn-${w}`}
                        >
                          <Pencil className="w-4 h-4" strokeWidth={3} />
                        </button>
                        <button
                          onClick={() => onRemove(w)}
                          disabled={busyWord === w}
                          className="w-9 h-9 rounded-full bg-rose-50 hover:bg-rose-100 text-[#F43F5E] flex items-center justify-center disabled:opacity-50"
                          title="Remove"
                          data-testid={`my-words-remove-${w}`}
                        >
                          {busyWord === w ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={3} />}
                        </button>
                      </div>
                    </>
                  )}
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
